import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { CnResult } from '@cainiaofe/cn-ui';
import { CnChart } from '@/components/cn-chart';
import {
  calculateAdvancedConfig,
  calculateFeature,
  getChartCommonStyle,
  isDesignMode,
} from '@/lowcode/common/util/util';
import useFilterSearchListener from '../common/hooks/useFilterSearchListener';
import { CnLineChartProps } from '@/lowcode/common/comoponents/cn-line-chart/type';
import {
  getLineCharOptions,
  getLineChartSeries,
} from '@/lowcode/common/comoponents/cn-line-chart/util';
import { FeaturePosition } from '@/types/feature-position';
import useStopLoop from '@/lowcode/common/hooks/useStopLoop';
import { getIntervalFields } from '@/lowcode/common/setter/interval-setter/util';
import { useData } from '@/lowcode/common/hooks/useData';
import { EmptyData } from '../common/empty-data';
import WrapperContainer from '../common/wrapper-container';

/**
 * @description 折线图
 * - xField：x轴
 * - yField：y轴
 * - categoryField：分类字段
 * 折线图只能有一个x轴；折线图可以有多个y轴，或者1个y轴字段+1个分类字段
 */
const CnLineChart = forwardRef((props: CnLineChartProps, ref) => {
  const { dataFrom, filterConfig, chartStyle, _context, forceRequest } = props || {};
  const { requestConfig } = dataFrom || {};
  const intervalFields = getIntervalFields(requestConfig);
  const { xField, yField } = intervalFields;
  const xFieldName = xField?.[0]?.fieldName;
  const { _bindFilter } = filterConfig || {};

  const {
    data: listData,
    error,
    loading,
    refreshData: sendRequest,
  } = useData({ config: { ...props, isDesign: isDesignMode(props) }, source: 'chart' });

  useFilterSearchListener({
    _bindFilter,
    sendRequest,
    _context,
  });

  useStopLoop({
    _nodeId: props?._nodeId,
  });

  useEffect(() => {
    if (forceRequest) {
      sendRequest();
    }
  }, [forceRequest]);

  useImperativeHandle(ref, () => ({
    load: sendRequest,
  }));

  const renderMain = () => {
    if (!listData || !xFieldName || !yField) {
      return (
        <EmptyData
          height={chartStyle.height}
          emptyImg={
            'https://img.alicdn.com/imgextra/i1/O1CN01I1VMZY24qUPhakk0p_!!6000000007442-2-tps-500-260.png'
          }
        />
      );
    }

    if (!listData?.length) {
      return <CnResult type="empty" title="数据为空" subTitle="暂无数据" />;
    }

    const defaultOptions = {
      ...getLineChartSeries(listData, intervalFields, chartStyle),
      ...getLineCharOptions(chartStyle),
    };

    const finalOptions = calculateAdvancedConfig({
      props,
      options: calculateFeature({
        props,
        options: defaultOptions,
        position: FeaturePosition.line,
        sendRequest,
      }),
      state: _context?.state,
    });

    return <CnChart {...getChartCommonStyle(props)} {...finalOptions} />;
  };

  return (
    <WrapperContainer loading={loading} error={error}>
      {renderMain()}
    </WrapperContainer>
  );
});

CnLineChart.displayName = 'CnLineChartPro';
export default CnLineChart;

export { CnLineChart };
